import { Component, EventEmitter, Input, Output, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { FileSelectionService } from 'app/shared/services/file-selection/file-selection.service';
import { VoiceToTextService } from 'app/shared/services/voice-to-text.service';
import { FeatureFlagsService } from 'app/shared/services/feature-flags/feature-flags.service';
import { FEATURE_FLAG_DEO_AGENT_ATTACH_FILE, FEATURE_FLAG_DEO_AGENT_LIVE_TRANSCRIPTION } from 'app/shared/services/feature-flags/feature-flags.constants';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'deo-ai-assistant-welcome-screen',
  standalone: false,
  templateUrl: './deo-ai-assistant-welcome-screen.component.html',
  styleUrls: ['./deo-ai-assistant-welcome-screen.component.scss']
})
export class DeoAiAssistantWelcomeScreenComponent implements OnInit, OnDestroy {
  @Input() userName = 'User';
  @Input() currentMessage = '';
  @Input() selectedFile: File | null = null;
  @Input() isLoading = false;
  @Input() isTyping = false;
  @Input() isListening = false;
  @Input() beforeUpload: (file: NzUploadFile) => boolean = () => false;

  @Output() currentMessageChange = new EventEmitter<string>();
  @Output() send = new EventEmitter<{
    messageContent: string;
    attachment?: { name: string; size: number; type: string };
  }>();
  @Output() fileRemoved = new EventEmitter<void>();
  @Output() voiceToggled = new EventEmitter<void>();

  private _baseText = '';
  voiceError: string | null = null;
  isLiveTranscriptionEnabled = false;
  isAttachFileEnabled = false;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _fileSelectionService: FileSelectionService,
    private readonly _voiceService: VoiceToTextService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _featureFlagsService: FeatureFlagsService
  ) {}

  ngOnInit(): void {
    this.isLiveTranscriptionEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_LIVE_TRANSCRIPTION);
    this.isAttachFileEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_ATTACH_FILE);

    this._voiceService.isListening$.pipe(takeUntil(this._destroy$)).subscribe(isListening => {
      this.isListening = isListening;
      this._cdr.markForCheck();
    });

    this._voiceService.transcription$.pipe(takeUntil(this._destroy$)).subscribe(transcription => {
      if (transcription.isFinal) {
        this._baseText = this._baseText ? this._baseText + ' ' + transcription.transcript : transcription.transcript;
        this.currentMessage = this._baseText;
      } else {
        this.currentMessage = this._baseText ? this._baseText + ' ' + transcription.transcript : transcription.transcript;
      }
      this._cdr.markForCheck();
    });

    this._voiceService.error$.pipe(takeUntil(this._destroy$)).subscribe(error => {
      this.voiceError = error;
      this._cdr.markForCheck();

      if (error) {
        setTimeout(() => {
          this.voiceError = null;
          this._cdr.markForCheck();
        }, 5000);
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  getGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  }

  getFileIcon(fileName: string): string {
    return this._fileSelectionService.getFileIcon(fileName);
  }

  formatFileSize(bytes: number): string {
    return this._fileSelectionService.formatFileSize(bytes);
  }

  onMessageChange(value: string): void {
    this.currentMessage = value;
    this.currentMessageChange.emit(value);
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
    }
  }

  onSend(): void {
    if (!this.currentMessage.trim() || this.isLoading) {
      return;
    }

    const messageContent = this.currentMessage.trim();
    const attachment = this.selectedFile
      ? {
          name: this.selectedFile.name,
          size: this.selectedFile.size,
          type: this.selectedFile.type
        }
      : undefined;

    this.send.emit({ messageContent, attachment });

    this.currentMessage = '';
    this._baseText = '';
    this.currentMessageChange.emit('');
    this.fileRemoved.emit();
  }

  onRemoveFile(): void {
    this.selectedFile = this._fileSelectionService.removeFile();
    this.fileRemoved.emit();
    this._cdr.markForCheck();
  }

  onVoiceToggle(): void {
    if (this.isTyping) {
      return;
    }

    if (this.isListening) {
      this._voiceService.stopListening();
      this.voiceToggled.emit();
      return;
    }

    this.voiceError = null;
    this._baseText = this.currentMessage;
    this._voiceService.startListeningFromConfiguration();
    this.voiceToggled.emit();
  }
}
